import React from 'react';
import { observer } from 'mobx-react';

import { Range, RosterZoom } from '..';

import DateTimeService from '@app/Services/DateTimeService';

import { VirtualHorizontalExpander } from '.';
import { VirtualDateMonth } from './VirtualDateMonth';
import { applyDecorators } from "@app/Helpers/Decorator";

export type DrawDayCounterHandler = (date: Date) => number | null;
export type DrawDayExtensionsHandler = (date: Date) => JSX.Element | null;
export type DrawMonthExtensionsHandler = (date: Date) => JSX.Element | null;
export type DrawDayFilterHandler = (date: Date) => JSX.Element | null;
export type DrawDayCssClassNameHandler = (date: Date) => string;

type VirtualDaysListProps = {
    datesListRef: React.RefObject<HTMLDivElement>;
    scrollWrapperRef: React.RefObject<HTMLDivElement>;
    style: React.CSSProperties;
    days: Date[];
    virtualWidth: number;
    allowedDateRange?: Array<Range<Date>> | Range<Date> | null;
    scrollLeft: number;
    cellWidth: number;
    zoom?: RosterZoom;
    showSwissTime?: boolean;
    majorMonth?: Date | null;

    onRenderCustomDate?: DrawDayExtensionsHandler;
    onRenderDayExtensions?: DrawDayExtensionsHandler;
    onRenderDayCssClassName?: DrawDayCssClassNameHandler;
    onRenderMonthExtensions?: DrawMonthExtensionsHandler;
    onRenderDailyFigures?: DrawDayExtensionsHandler;
    onRenderRosterFilter?: DrawDayExtensionsHandler;
};

@observer
export class VirtualDaysList extends React.Component<VirtualDaysListProps> {
    
    constructor (props:VirtualDaysListProps) {
        super(props);
        applyDecorators(this);
    }
    
    render () {
        const {
            style,
            days,
            virtualWidth,
            scrollLeft,
            cellWidth,
            datesListRef,
            scrollWrapperRef,
            showSwissTime,
            zoom,
            onRenderCustomDate,
            onRenderDayExtensions,
            onRenderDayCssClassName,
            onRenderMonthExtensions,
            onRenderDailyFigures,
            onRenderRosterFilter
        } = this.props;
        const dates: Array<JSX.Element> = [];
        let newMonth = true;
        let time: JSX.Element | null = null;

        days.forEach((day) => {
            const curMonth = new Date(day.getFullYear(), day.getMonth() + 1, 0);

            if (newMonth) {
                const lastInRange = days[days.length - 1];
                const daysCount = this._getDaysCount(day, lastInRange);
                const width = daysCount * cellWidth;

                dates.push(
                    <VirtualDateMonth
                        key={day.getFullYear() + '_' + day.getMonth()}
                        datesListRef={datesListRef}
                        scrollWrapperRef={scrollWrapperRef}
                        day={day}
                        scrollLeft={scrollLeft}
                        cellWidth={cellWidth}
                        zoom={zoom}
                        width={width}
                        onRenderMonthExtensions={onRenderMonthExtensions}
                        highlight={this._isMajorMonth(day)}
                    />
                );
                newMonth = false;
            }
            if (curMonth.getDate() === day.getDate()) {
                newMonth = true;
            }
            const dateClass = ['person-grid-date'];
            if (day.getUTCDate() === 1) dateClass.push('person-grid-period');
            if (day.getUTCDay() === 6 || day.getUTCDay() === 0) dateClass.push('weekend');
            if (DateTimeService.isSameDate(DateTimeService.today(), day)) dateClass.push('active');
            if (this._isDateAllowed(day)) dateClass.push('date-allowed');
            if (onRenderDayCssClassName) dateClass.push(onRenderDayCssClassName(day));
            if (zoom != null && zoom !== RosterZoom.x16) {
                const today = DateTimeService.today();
                const nowDate = DateTimeService.now();
                const now = showSwissTime ? DateTimeService.getSwissDate(nowDate) : nowDate;
                const hours: string[] = [];
                let zoomClass = '';
                if (zoom === RosterZoom.x4) zoomClass += 'zoom-x4';
                if (zoom === RosterZoom.x8) zoomClass += 'zoom-x8';
                for (let i = 1; i <= 24; i++) {
                    hours.push(i < 10 ? `0${i}` : `${i}`);
                }
                time = <div className={`person-grid-date-time ${zoomClass}`}>
                    {hours.map(h => <div className="day-caption-hour" key={h}><span>{h}</span></div>)}
                    {DateTimeService.isSameDate(DateTimeService.today(), day) &&
                        <span className="day-caption-now" style={{ left: `${((DateTimeService.diffMinutes(now, today) / (24 * 60)) * 100).toFixed(3)}%` }}>{DateTimeService.toUiTime(now)}{showSwissTime ? '.' : ''}</span>
                    }
                </div>;
            }
            dates.push(
                <div className={dateClass.join(' ')} key={day.getTime()}>
                    <div className="person-grid-date-day">
                        {onRenderCustomDate
                            ? onRenderCustomDate(day)
                            : <span className="person-grid-date-day-count">
                                {this._getDay(day)}
                            </span>
                        }
                        {onRenderDayExtensions && onRenderDayExtensions(day)}
                    </div>
                    {onRenderDailyFigures && onRenderDailyFigures(day)}
                    {onRenderRosterFilter && onRenderRosterFilter(day)}
                    {time}
                </div>
            );
        });

        return (
            <div className="virtual-list">
                <VirtualHorizontalExpander virtualWidth={virtualWidth}/>
                <div className="virtual-list-items" style={style}>
                    {dates}
                </div>
            </div>
        );
    }

    private _getDay (date: Date): string {
        const d = date.getUTCDate().toString();
        return d.length === 1 ? '0' + d : d;
    }

    private _getDaysCount (date: Date, lastInRange: Date) {
        const currentMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        let daysCount = currentMonth.getDate() - date.getDate() + 1;
        if (currentMonth.getMonth() === lastInRange.getMonth() && lastInRange.getDate() < daysCount) {
            return lastInRange.getDate();
        }

        return daysCount;
    }

    private _isMajorMonth(date: Date): boolean {
        const {majorMonth} = this.props;
        if (!majorMonth) {
            return false;
        }

        return majorMonth.getFullYear() === date.getFullYear() && majorMonth.getMonth() === date.getMonth();
    }

    private _isDateAllowed(dutyDay: Date) {
        const { allowedDateRange } = this.props;
        if (Array.isArray(allowedDateRange)) {
            return allowedDateRange?.some(range => range.from <= dutyDay && range.to >= dutyDay);
        }

        return allowedDateRange && allowedDateRange.from <= dutyDay && allowedDateRange.to >= dutyDay;
    }
}
